import { MediaMatcher } from '@angular/cdk/layout';
import { OverlayContainer } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injectable,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { RippleGlobalOptions } from '@angular/material/core';
import { Title } from '@angular/platform-browser';
import {
    NavigationEnd,
    Router,
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AppStateFacade } from '@app-state';
import {
    PageMetadata,
    PageMetadataRegistry,
} from '@app/router';
import { UiPersistentNotificationComponent } from '@components';
import { TranslateService } from '@ngx-translate/core';
import {
    AnalyticsService,
    StorageService,
} from '@services';
import { isInternetExplorer } from '@uipath/angular/utilities';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import {
    combineLatest,
    from,
    fromEvent,
    merge,
} from 'rxjs';
import {
    filter,
    map,
    startWith,
    switchMap,
} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AppRippleOptions implements RippleGlobalOptions {
    disabled = false;
}

@Component({
    selector: 'ui-app',
    templateUrl: './main.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class MainComponent implements OnInit {
    @HostBinding('@.disabled')
    public animationsDisabled = false;

    @HostBinding('class')
    public languageClass: string;

    @HostBinding('class.is-internet-explorer')
    public get isInternetExplorer() {
        return isInternetExplorer();
    }

    @ViewChild(UiPersistentNotificationComponent, { static: true })
    private _persistentNotification!: UiPersistentNotificationComponent;

    constructor(
        private _analytics: AnalyticsService,
        private _translateService: TranslateService,
        private _overlayContainer: OverlayContainer,
        private _renderer: Renderer2,
        private _appRippleOptions: AppRippleOptions,
        private _title: Title,
        private _appState: AppStateFacade,
        private _router: Router,
        private _swUpdate: SwUpdate,
        storageService: StorageService,
        mediaMatcher: MediaMatcher,
        pageRegistry: PageMetadataRegistry,
    ) {
        this.languageClass = this._translateService.currentLang;

        const mediaReducedMotion = mediaMatcher.matchMedia('(prefers-reduced-motion: reduce)');
        _appRippleOptions.disabled = mediaReducedMotion.matches || !!isInternetExplorer();
        this._onPrefersReducedMotionChanged(mediaReducedMotion.matches);

        merge(
            fromEvent<MediaQueryListEvent>(mediaReducedMotion, 'change')
                .pipe(
                    map(e => e.matches),
                ),
            storageService.watch('prefers-reduced-motion')
                .pipe(
                    filter(event => event.newValue !== 'null'),
                    map(event => Boolean(event.newValue)),
                ),
            fromEvent<CustomEvent>(document.body, 'prefersReducedMotion')
                .pipe(
                    map(event => event.detail),
                ),
        ).subscribe(this._onPrefersReducedMotionChanged);

        this._router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                map(() => this._router.routerState.snapshot.root.queryParamMap.get('lang')),
                filter(languageCode => !!languageCode),
                switchMap(languageCode => _appState.language.set(languageCode!)),
            ).subscribe();

        combineLatest([
            this._translateService
                .onLangChange
                .pipe(startWith(void 0)),
            pageRegistry
                .activePage$.pipe(
                    filterNullOrUndefined(),
                ),
        ])
            .subscribe(([_, page]) => this._setDocumentTitle(page));
    }

    ngOnInit() {
        this._analytics.track('Application Load', { page: 'N/A' });

        this._swUpdate.available
            .pipe(
                switchMap(() => {
                    this._persistentNotification.show = true;
                    return this._persistentNotification.closed.pipe(
                        filter(close => close === 'confirm'),
                    );
                }),
                switchMap(() =>
                    from(this._swUpdate.activateUpdate()),
                ),
            )
            .subscribe(() =>
                location.reload(),
            );
    }

    private _onPrefersReducedMotionChanged = (prefersReducedMotion: boolean) => {
        this.animationsDisabled = prefersReducedMotion;
        this._renderer.setProperty(
            this._overlayContainer.getContainerElement(),
            '@.disabled',
            this.animationsDisabled,
        );
        this._appRippleOptions.disabled = prefersReducedMotion || !!isInternetExplorer();
    }

    private _setDocumentTitle(page: PageMetadata) {
        const documentTitle = [
            !!page.translationKey
                ? this._translateService.instant(page.translationKey)
                : page.title,
            this._appState.authConfig.value.serverSettings.baseTitle,
        ].join(' - ');

        this._title.setTitle(documentTitle);
    }
}
